import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeature } from '~/store/entitlements/useFeature';
import type { FeatureEntitlement } from '~/store/entitlements/interfaces';

import UpgradePrompt from '~/atomic/molecules/UpgradePrompt/view';

interface PropsFeatureGate {
  feature: string;
  children: ReactNode | ((entitlement: FeatureEntitlement) => ReactNode);
  fallback?: ReactNode | ((entitlement: FeatureEntitlement) => ReactNode);
  blockOnQuotaExhausted?: boolean;
}

export default function FeatureGate({
  feature,
  children,
  fallback,
  blockOnQuotaExhausted = true,
}: PropsFeatureGate) {
  const navigate = useNavigate();
  const { entitlement, isAvailable, isQuotaExhausted } = useFeature(feature);
  const blocked = !isAvailable || (blockOnQuotaExhausted && isQuotaExhausted);

  if (!blocked) {
    return typeof children === 'function' ? <>{children(entitlement)}</> : <>{children}</>;
  }

  if (fallback !== undefined) {
    return typeof fallback === 'function' ? <>{fallback(entitlement)}</> : <>{fallback}</>;
  }

  return (
    <UpgradePrompt
      featureSlug={feature}
      entitlement={entitlement}
      onClickCta={() => navigate('/app/subscribe')}
    />
  );
}
